/**
 * Playback Telemetry — lightweight, localStorage-backed metrics for tuning
 * unbroken audio across platforms (iOS Safari, Android Chrome, desktop).
 *
 * Persists the last N events so we can inspect them from the console or
 * ship them to an analytics endpoint later.
 *
 * Usage:
 *   import { pb } from "@/lib/playbackTelemetry";
 *   pb.trackStart(trackId);
 *   pb.firstAudio(trackId);     // when 'playing' fires
 *   pb.crossfadeStart(from, to);
 *   pb.crossfadeEnd(from, to, ok);
 *   pb.dropout(trackId, reason, extra);
 *   pb.stallRecovery(trackId, attempt, strategy, recovered);
 */

const STORAGE_KEY = "llf_playback_telemetry";
const MAX_ENTRIES = 100;

// ── Platform fingerprint (computed once) ─────────────────────────────────────
function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac/i.test(ua)) return "mac";
  if (/Win/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";
  return "unknown";
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (/CriOS/i.test(ua)) return "chrome-ios";
  if (/FxiOS/i.test(ua)) return "firefox-ios";
  if (/EdgiOS/i.test(ua)) return "edge-ios";
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return "safari";
  if (/Edg\//i.test(ua)) return "edge";
  if (/Firefox/i.test(ua)) return "firefox";
  if (/Chrome/i.test(ua)) return "chrome";
  return "other";
}

const platform = detectPlatform();
const browser = detectBrowser();
const network = () => (navigator as any).connection?.effectiveType ?? "unknown";

// ── Types ────────────────────────────────────────────────────────────────────
export type TelemetryEventType =
  | "track_start"
  | "first_audio"
  | "crossfade_start"
  | "crossfade_end"
  | "dropout"
  | "stall_recovery"
  | "stall_resolved"
  | "error_skip"
  | "queue_advance"
  | "queue_rebuild"
  | "whiplash_swap"
  | "skip_next"
  | "skip_prev"
  | "play_track"
  | "stale_guard"
  | "silence_all";

export interface TelemetryEvent {
  ts: number;
  type: TelemetryEventType;
  platform: string;
  browser: string;
  network: string;
  trackId?: string;
  data?: Record<string, unknown>;
}

// ── Persistence ──────────────────────────────────────────────────────────────
function readLog(): TelemetryEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeLog(log: TelemetryEvent[]) {
  try {
    if (log.length > MAX_ENTRIES) log.splice(0, log.length - MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch { /* quota / unavailable */ }
}

function emit(type: TelemetryEventType, trackId?: string, data?: Record<string, unknown>) {
  const evt: TelemetryEvent = {
    ts: Date.now(),
    type,
    platform,
    browser,
    network: network(),
    trackId,
    data,
  };
  const log = readLog();
  log.push(evt);
  writeLog(log);

  // eslint-disable-next-line no-console
  console.info(
    `%c[PlaybackTel]`,
    "color:#0af;font-weight:bold",
    type,
    trackId ? `track=${trackId}` : "",
    data ? data : "",
  );
}

// ── In-flight timing map ─────────────────────────────────────────────────────
const startTimes = new Map<string, number>();

// ── Public API ───────────────────────────────────────────────────────────────
export const pb = {
  /** Called when we begin loading / playing a new track */
  trackStart(trackId: string) {
    startTimes.set(trackId, performance.now());
    emit("track_start", trackId);
  },

  /** Called on the first 'playing' event for a track — captures time-to-first-audio */
  firstAudio(trackId: string) {
    const start = startTimes.get(trackId);
    const ttfa = start != null ? Math.round(performance.now() - start) : null;
    startTimes.delete(trackId);
    emit("first_audio", trackId, { ttfaMs: ttfa });
  },

  /** Called when crossfade ramp begins */
  crossfadeStart(fromId: string, toId: string) {
    startTimes.set(`xf_${fromId}_${toId}`, performance.now());
    emit("crossfade_start", fromId, { toId });
  },

  /** Called when crossfade finishes (or fails) */
  crossfadeEnd(fromId: string, toId: string, success: boolean) {
    const key = `xf_${fromId}_${toId}`;
    const start = startTimes.get(key);
    const durationMs = start != null ? Math.round(performance.now() - start) : null;
    startTimes.delete(key);
    emit("crossfade_end", fromId, { toId, success, durationMs });
  },

  /** Called on any audio dropout / interruption */
  dropout(trackId: string, reason: string, extra?: Record<string, unknown>) {
    emit("dropout", trackId, { reason, ...extra });
  },

  /** Called after each stall-recovery attempt */
  stallRecovery(trackId: string, attempt: number, strategy: string, recovered: boolean) {
    emit("stall_recovery", trackId, { attempt, strategy, recovered });
  },

  /**
   * Called when a stall incident is fully resolved — captures the complete
   * picture: how many retries ran, which strategy finally succeeded, and
   * whether playback resumed smoothly (timeupdate fired within 2s of recovery).
   */
  stallResolved(trackId: string, totalAttempts: number, winningStrategy: string, stallDurationMs: number, smoothResume: boolean) {
    emit("stall_resolved", trackId, { totalAttempts, winningStrategy, stallDurationMs, smoothResume });
  },

  /** Called when a track is skipped due to a load error */
  errorSkip(trackId: string, errorCode: number | null, errorMsg: string | null) {
    emit("error_skip", trackId, { errorCode, errorMsg });
  },

  /** Called when advanceToNext picks the next track from the queue */
  queueAdvance(fromId: string | null, toId: string, queueIdx: number, queueLen: number) {
    emit("queue_advance", toId, { fromId, queueIdx, queueLen });
  },

  /** Called when the shuffle queue is rebuilt (new cycle or library change) */
  queueRebuild(reason: "cycle_exhausted" | "cycle_reset" | "library_changed", newLen: number, playedCount: number, totalTracks: number) {
    emit("queue_rebuild", undefined, { reason, newLen, playedCount, totalTracks });
  },

  /** Called when the whiplash-swap heuristic reorders two adjacent tracks */
  whiplashSwap(currentId: string, originalNextId: string, swappedToId: string, emotionalDist: number) {
    emit("whiplash_swap", currentId, { originalNextId, swappedToId, emotionalDist: Math.round(emotionalDist * 1000) / 1000 });
  },

  /** Called on skipNext / skipPrev user actions */
  skip(direction: "next" | "prev", fromId: string | null, toId: string | null) {
    emit(direction === "next" ? "skip_next" : "skip_prev", fromId ?? undefined, { toId });
  },

  /** Called when playTrack switches to a specific track */
  playTrackSwitch(fromId: string | null, toId: string, injected: boolean) {
    emit("play_track", toId, { fromId, injected });
  },

  /** Called when a stale-event guard blocks a handler from firing */
  staleGuard(handler: string, trackId: string, reason: string) {
    emit("stale_guard", trackId, { handler, reason });
  },

  /** Called when silenceAll kills active audio sources */
  silenceAll(currentId: string | null, hadCrossfade: boolean, hadPending: boolean, hadStallTimer: boolean) {
    emit("silence_all", currentId ?? undefined, { hadCrossfade, hadPending, hadStallTimer });
  },

  // ── Reporting helpers ────────────────────────────────────────────────────
  /** Returns the full telemetry log */
  getLog(): TelemetryEvent[] { return readLog(); },

  /** Returns a summary object for console inspection */
  summary() {
    const log = readLog();
    const crossfadeStarts = log.filter(e => e.type === "crossfade_start").length;
    const crossfadeEnds = log.filter(e => e.type === "crossfade_end");
    const crossfadeOk = crossfadeEnds.filter(e => e.data?.success === true).length;
    const ttfaEvents = log.filter(e => e.type === "first_audio" && e.data?.ttfaMs != null);
    const ttfaValues = ttfaEvents.map(e => e.data!.ttfaMs as number);
    const dropouts = log.filter(e => e.type === "dropout").length;
    const stalls = log.filter(e => e.type === "stall_recovery");
    const stallsRecovered = stalls.filter(e => e.data?.recovered === true).length;
    const errorSkips = log.filter(e => e.type === "error_skip").length;

    const byPlatform = (evts: TelemetryEvent[]) => {
      const m: Record<string, number> = {};
      for (const e of evts) { m[e.platform] = (m[e.platform] ?? 0) + 1; }
      return m;
    };

    return {
      totalEvents: log.length,
      crossfade: { started: crossfadeStarts, completed: crossfadeOk, failed: crossfadeEnds.length - crossfadeOk, rate: crossfadeStarts > 0 ? `${Math.round(crossfadeOk / crossfadeStarts * 100)}%` : "n/a" },
      ttfa: {
        count: ttfaValues.length,
        avgMs: ttfaValues.length > 0 ? Math.round(ttfaValues.reduce((a, b) => a + b, 0) / ttfaValues.length) : null,
        p50Ms: ttfaValues.length > 0 ? ttfaValues.sort((a, b) => a - b)[Math.floor(ttfaValues.length / 2)] : null,
        p95Ms: ttfaValues.length > 0 ? ttfaValues.sort((a, b) => a - b)[Math.floor(ttfaValues.length * 0.95)] : null,
        byPlatform: byPlatform(ttfaEvents),
      },
      dropouts: { total: dropouts, byPlatform: byPlatform(log.filter(e => e.type === "dropout")) },
      stallRecovery: { total: stalls.length, recovered: stallsRecovered, byPlatform: byPlatform(stalls) },
      stallResolution: (() => {
        const resolved = log.filter(e => e.type === "stall_resolved");
        if (resolved.length === 0) return { incidents: 0 };
        const attempts = resolved.map(e => (e.data?.totalAttempts as number) ?? 0);
        const durations = resolved.map(e => (e.data?.stallDurationMs as number) ?? 0);
        const smooth = resolved.filter(e => e.data?.smoothResume === true).length;
        const strategies: Record<string, number> = {};
        for (const e of resolved) {
          const s = (e.data?.winningStrategy as string) ?? "unknown";
          strategies[s] = (strategies[s] ?? 0) + 1;
        }
        return {
          incidents: resolved.length,
          avgAttempts: Math.round(attempts.reduce((a, b) => a + b, 0) / attempts.length * 10) / 10,
          avgDurationMs: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
          smoothResumeRate: `${Math.round(smooth / resolved.length * 100)}%`,
          winningStrategies: strategies,
          byPlatform: byPlatform(resolved),
        };
      })(),
      errorSkips,
      platform,
      browser,
    };
  },

  /** Clear all telemetry data */
  clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  },
};

// Expose globally for console debugging
if (typeof window !== "undefined") {
  (window as any).__pbTel = pb;
}
